import { useMemo, useRef, useState } from 'react'

import type { UseQueryResult } from '@tanstack/react-query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'

import type { PriceValidationRequest } from '@/api/ecomProduct'
import { ecomProductResources } from '@/api/ecomProduct'
import queries from '@/api/queries'
import type { GetMandatoryProductsResponse, Product } from '@/components/ecom-policy/types'
import { useCart } from '@/contexts/cart/cart-context'
import { ecomPolicyResources } from '@/resources/ecom-policy'
import type {
  GreenpoolOrderPayload,
  MasterOrderAssetRequest,
  MasterOrderRequest,
} from '@/resources/ecom-policy-orders'
import { ecomPolicyOrdersResources } from '@/resources/ecom-policy-orders'
import { APP_ROUTES } from '@/util/appRoutes'

export const useCartOrder = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const idempotencyKey = useRef(uuidv4())

  const {
    items,
    selectedAddressId,
    customAddress,
    isPrivateAddress,
    isTermsAccepted,
    getItemsGroupedByPolicy,
    getPolicyIds,
    clearCart,
    clearConfirmData,
  } = useCart()
  let { assetsState }: { assetsState: MasterOrderAssetRequest[] } = location.state || {}
  const [invalidProductPrices, setInvalidProductPrices] =
    useState<Record<number, { oldPrice: number; newPrice: number }>>()

  const { data: policyData, isLoading: isLoadingPolicy } = useQuery(
    queries.policy(Number(Object.keys(getItemsGroupedByPolicy())[0])),
    () => ecomPolicyResources.get(Number(Object.keys(getItemsGroupedByPolicy())[0])),
    {
      enabled: items.length > 0,
    }
  )
  const { returnAssets, buyoutAssets } = useMemo(() => {
    const assets = assetsState.filter(item => getPolicyIds().includes(item.policyId))
    const buyoutAssets = assets.filter(item => item.decision === 'BUYOUT')
    const returnAssets = assets.filter(item => item.decision === 'RETURN')
    return { returnAssets, buyoutAssets }
  }, [assetsState, getPolicyIds])

  // useMemo to get address and check if its a custom address
  const address = useMemo(() => {
    if (!selectedAddressId) return null
    if (selectedAddressId === -2) {
      if (!customAddress?.address || !customAddress?.city || !customAddress?.zip) return null
      return {
        address: customAddress.address,
        city: customAddress.city,
        zip: customAddress.zip,
      }
    }
    return policyData?.addresses.find(addr => addr.id === selectedAddressId) || null
  }, [selectedAddressId, customAddress, policyData])

  const isOrderValid = useMemo(() => {
    const itemsGrouped = getItemsGroupedByPolicy()
    if (Object.keys(itemsGrouped).length === 0) return false

    if (isTermsAccepted === false) return false

    const isOnlyGreenpoolItems = items.every(i => i.isGreenpoolProduct)
    if (isOnlyGreenpoolItems) return true

    if (!selectedAddressId) return false
    if (selectedAddressId === -2) {
      if (!customAddress?.address || !customAddress?.city || !customAddress?.zip) return false
    }

    return true
  }, [getItemsGroupedByPolicy, items, isTermsAccepted, selectedAddressId, customAddress])

  /*
   * Whenever the user navigate to the "Confirm order" page, we need to validate the prices of the selected products
   * and accessories.
   */
  const priceValidationMutation = useMutation(ecomProductResources.postProductValidation, {
    onSuccess: response => {
      const invalidProductPrices: Record<number, { oldPrice: number; newPrice: number }> = {}
      const selectedProducts = items
        .filter(
          item => Boolean(item.product) && !item.product?.isMandatory && !item.isGreenpoolProduct
        )
        .map(item => item.product) as Product[]

      for (const product of selectedProducts || []) {
        const validationResult = response.validationResults.find(p => p.productId === product.id)

        if (!validationResult?.isValid) {
          invalidProductPrices[product.id] = {
            oldPrice: product.price,
            newPrice: validationResult?.latestPrice ?? -1,
          }
        }

        // FIX: Incorrect total cost for price with VAT
        setInvalidProductPrices(invalidProductPrices)

        if (invalidProductPrices && Object.keys(invalidProductPrices).length > 0) {
          queryClient.invalidateQueries(queries.policyList())
        }
      }
    },
    onSettled: () => {
      navigate('/cart/confirm', { state: { assetsState: assetsState } })
    },
  })
  const doPriceValidation = () => {
    const payload: PriceValidationRequest = {
      products:
        items
          .filter(
            item => Boolean(item.product) && !item.product?.isMandatory && !item.isGreenpoolProduct
          )
          .map(p => ({ id: Number(p.productId), proposedPrice: p.product?.priceNet || 0 })) || [],
    }
    if (payload.products.length > 0) {
      priceValidationMutation.mutate(payload)
    }
  }

  const { mutate: mutateMasterOrder, isLoading: isMasterOrderSubmitting } = useMutation(
    ecomPolicyOrdersResources.createMasterOrder,
    {
      onSuccess: () => {
        clearCart()

        clearConfirmData()
        navigate(APP_ROUTES.ecomPolicyList)
      },
      onSettled: () => {
        queryClient.refetchQueries(queries.policyList())
        queryClient.invalidateQueries({
          queryKey: ['policy'],
          refetchType: 'all',
        })
      },
    }
  )
  const { mutateAsync: mutateGreenpoolOrderAsync, isLoading: isGreenpoolOrderSubmitting } =
    useMutation(ecomPolicyOrdersResources.createGreenpoolOrder, {
      onSuccess: data => {
        clearCart()

        clearConfirmData()

        if (!data?.isMasterOrder) {
          navigate(APP_ROUTES.ecomPolicyList)
        }
      },
      onSettled: data => {
        if (!data?.isMasterOrder) {
          queryClient.refetchQueries(queries.policyList())
          queryClient.invalidateQueries({
            queryKey: ['policy'],
            refetchType: 'all',
          })
        }
      },
    })

  const onCompleteOrder = async (
    mandatoryProductsQueries: UseQueryResult<GetMandatoryProductsResponse>[]
  ) => {
    const assets = [...returnAssets, ...buyoutAssets]
    const isOnlyGreenpoolItems = items.every(i => i.isGreenpoolProduct)
    if (isOnlyGreenpoolItems && (!isTermsAccepted || assets.some(v => v.decision === 'NONE'))) {
      return
    }
    if (
      !isOnlyGreenpoolItems &&
      (!selectedAddressId ||
        !address ||
        !isTermsAccepted ||
        assets.some(v => v.decision === 'NONE'))
    )
      return

    const itemsGrouped = getItemsGroupedByPolicy()
    const orderLines: MasterOrderRequest['orderLines'] = []
    const greenpoolOrders: GreenpoolOrderPayload[] = []

    Object.entries(itemsGrouped).forEach(([policyId, items]) => {
      items.forEach(item => {
        if (item.isGreenpoolProduct) {
          greenpoolOrders.push({
            policyId: Number(item.policyId),
            orderPayload: {
              assetId: Number(item.asset?.id || item.productId),
              assets: [],
            },
          })
        } else {
          orderLines.push({
            productCatalogueId: Number(item.productId),
            quantity: item.qty,
            policyId: Number(policyId),
          })
        }
      })

      const mandatoryProducts =
        mandatoryProductsQueries.find(q => q.data && q.data.policyId === Number(policyId))?.data
          ?.mandatoryProducts || []
      if (mandatoryProducts.length > 0) {
        mandatoryProducts.forEach(product => {
          orderLines.push({
            productCatalogueId: Number(product.id),
            quantity: 1,
            policyId: Number(policyId),
          })
        })
      }
    })
    let greenpoolPayloads = greenpoolOrders
    const isMasterOrder = orderLines.length > 0

    if (isMasterOrder) {
      if (!address) return
      const payload: MasterOrderRequest = {
        idempotencyKey: idempotencyKey.current,
        deliveryAddress: address.address,
        city: address.city,
        zip: address.zip,
        addressId: selectedAddressId === -2 ? undefined : selectedAddressId,
        isPrivateAddress: isPrivateAddress,
        orderLines,
        assets,
      }
      mutateMasterOrder(payload)
    } else {
      // Send assets with greenpool orders, if no master order
      greenpoolPayloads = greenpoolOrders.map(order => {
        const greenpoolAssets = assets.filter(asset => asset.policyId === Number(order.policyId))

        if (order.policyId) {
          return {
            ...order,
            orderPayload: {
              ...order.orderPayload,
              assets:
                greenpoolAssets.map(asset => ({
                  id: asset.assetId,
                  is_buyout: asset.decision === 'BUYOUT',
                })) || [],
            },
          }
        }
        return order
      })
    }
    if (greenpoolOrders.length > 0) {
      await Promise.all(
        greenpoolPayloads.map(order => mutateGreenpoolOrderAsync({ ...order, isMasterOrder }))
      )
    }
  }

  return {
    returnAssets,
    buyoutAssets,
    isOrderValid,
    isLoadingPolicy,
    isMasterOrderSubmitting,
    isGreenpoolOrderSubmitting,
    invalidProductPrices,
    doPriceValidation,
    onCompleteOrder,
  }
}
